<template>
  <div class="animated fadeIn">
    <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
      <i class="fal fa-spinner fa-spin" /> Loading
    </div>
    <div v-else>
      <template v-if="schedule">
        <basic-heading class="mb-3">
          <span class="float-left mr-2">{{
            `${schedule_process_prefix} - ${sample_type.name} - ${schedule_date}`
          }}</span>
          <template #right-content>
            <header-button-group
              :button_config="button_config"
              @dropdownAction="dropdownAction"
            />
          </template>
        </basic-heading>
        <b-row class="mb-3">
          <b-col class="pr-1">
            <b-input-group prepend="Client">
              <b-form-input
                v-model="filret_client"
                placeholder="Filter..."
                debounce="300"
              />
            </b-input-group>
          </b-col>
          <b-col class="px-1">
            <b-input-group prepend="RFID">
              <b-form-input
                v-model="filret_rfid"
                placeholder="Filter..."
                debounce="300"
              />
            </b-input-group>
          </b-col>
          <b-col class="px-1">
            <b-input-group prepend="Turnaround">
              <b-form-input
                v-model="filret_turnaround"
                placeholder="Filter..."
                debounce="300"
              />
            </b-input-group>
          </b-col>
          <b-col class="px-1">
            <b-input-group prepend="Submission">
              <b-form-input
                v-model="filret_submission"
                placeholder="Filter..."
                debounce="300"
              />
            </b-input-group>
          </b-col>
          <b-col cols="3" class="text-right">
            <b-button-group>
              <b-button @click="clearFilters">
                <i class="fa fa-close" /> Clear
              </b-button>
              <b-button variant="primary">
                <i class="fa fa-filter" /> Filter
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>
        <b-row class="mb-3 pr-3">
          <b-col
            v-for="(process, index) in schedule.process_schedules"
            :key="index"
            class="pr-0"
          >
            <b-button
              :variant="picked_process === process.id ? 'primary' : ''"
              block
              @click="pickProcess(process.id)"
              >{{ process.schedulable_process.name }}</b-button
            >
          </b-col>
        </b-row>
        <b-tabs>
          <b-tab
            v-for="(task, index) in process_tasks"
            :key="index"
            :title="task.name"
            @click="changeTab"
            lazy
          >
            <b-row class="pr-3">
              <b-col
                v-for="(instance, instance_index) in getTaskInstances(task)"
                :key="instance_index"
                cols="3"
                class="mb-3 pr-0"
                @click="pickInstance(instance.id)"
              >
                <b-card
                  v-if="picked_instrument"
                  no-body
                  :class="`${
                    picked_instrument.instrument_instance_id === instance.id
                      ? 'picked'
                      : ''
                  } px-1 py-2 mb-0 pointer`"
                >
                  <div>{{ instance.name }}</div>
                  <div>{{ instance.instrument.name }}</div>
                  <instrument-capacity-bar
                    class="d-inline-block w-100"
                    :instrument_instance="instance"
                    :process="process_model"
                  />
                </b-card>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-row class="mb-3">
                  <b-col class="font-3xl">Schedule Samples</b-col>
                  <b-col cols="4" class="pr-0">
                    <b-input-group prepend="Instrument Jig">
                      <b-form-select
                        v-model="picked_instrument_jig"
                        :options="instrument_jig_options"
                        :state="!$v.picked_instrument_jig.$invalid"
                        value-field="data"
                        text-field="name"
                        @change="updateInstrumentJig"
                        invalid-feedback="Required"
                      />
                    </b-input-group>
                  </b-col>
                  <b-col cols="4">
                    <b-input-group prepend="Sample Set">
                      <b-form-select
                        v-model="selected_sample_set"
                        :options="sample_set_options"
                      />
                    </b-input-group>
                  </b-col>
                </b-row>
                <scheduled-samples-server-table
                  v-if="picked_instrument"
                  :filter="filter"
                  :picked_instrument="picked_instrument.instrument_instance_id"
                  :picked_instrument_jig="picked_instrument_jig"
                  :instrument_jig_collection="instrument_jig_collection"
                  :sample_collection="sample_collection"
                  :schedule_process="schedule_process"
                  :schedule_task="task"
                  @updateSchedule="fetchSchedule"
                />
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import InstrumentCapacityBar from "@/components/lab/instrument/InstrumentCapacityBar";
import ScheduledSamplesServerTable from "@/components/lab/schedule/ScheduledSamplesServerTable";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import ProcessScheduleModel from "@/models/lab/ProcessScheduleModel";
import { mapActions } from "vuex";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate/src";
import {
  getInitialInstanceData,
  getTabData,
  getTaskInstances,
} from "@/shared/utils";
import { getModel, getCollection } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

const ProcessScheduleRepository = LabRepositoryFactory.get("processSchedule");
const ScheduleRepository = LabRepositoryFactory.get("schedule");

export default {
  name: "ScheduleSamples",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    InstrumentCapacityBar,
    ScheduledSamplesServerTable,
  },
  mixins: [validationMixin, ToastNotifications],
  data() {
    return {
      loading: true,
      filter: {
        client: null,
        rfid: null,
        turnaround: null,
        submission: null,
        sample_set: "on_instrument",
      },
      instrument_jig_collection: null,
      sample_type: null,
      sample_collection: null,
      schedule: null,
      schedule_id: this.$route.params.schedule_id,
      picked_process: null,
      picked_instrument: null,
      picked_task: null,
      picked_instrument_jig: null,
      sample_set_options: [
        {
          value: null,
          text: "All (Sample Cohort)",
        },
        {
          value: "on_instrument",
          text: "Scheduled On Instrument",
        },
        {
          value: "not_on_instrument",
          text: "Not Scheduled On Instrument",
        },
        {
          value: "not_scheduled",
          text: "Not Scheduled",
        },
      ],
    };
  },
  computed: {
    schedule_date() {
      return moment(this.schedule.date).format("MMMM DD, YYYY");
    },
    schedule_process_prefix() {
      return this.schedule.process_schedules[0].schedulable_process_type.substring(
        4,
        8
      );
    },
    schedule_process() {
      const schedule_process = this.schedule.process_schedules.find(
        (process) => process.id === this.picked_process
      );
      if (schedule_process.instrument_instance_schedules) {

        this.UPDATE_SCHEDULE_SAMPLE_LIST(
          schedule_process.instrument_instance_schedules.map((item) => {
            return {
              sample_id: item.sample_id,
              instrument_id: item.instrument_id,
              instrument_instance_id: item.instrument_instance_id,
              instrument_jig_id: item.instrument_jig_id,
              schedulable_task_type: item.schedulable_task_type,
              schedulable_task_id: item.schedulable_task_id,
            };
          })
        );
      }
      return schedule_process;
    },
    process_tasks() {
      return this.schedule_process.schedulable_process.schedulable_tasks.filter(
        ({ id }) =>
          this.schedule_process.process_schedule_instrument_instances.find(
            ({ process_task_id }) => id === process_task_id
          )
      );
    },
    process_model() {
      return new ProcessScheduleModel(this.schedule_process);
    },
    instrument_id_list() {
      return this.schedule.process_schedules.reduce(
        (acc, { instrument_instances }) => {
          if (instrument_instances) {
            instrument_instances.forEach(({ instrument_id }) => {
              const check = acc.find((id) => id === instrument_id);
  
              !check && (acc = [...acc, instrument_id]);
            });
          }

          return acc;
        },
        []
      );
    },
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            title: "To Schedule list",
            variant: "primary",
            linkto: "/schedules/",
          },
          {
            icon: "fa-eye",
            title: "To Schedule",
            variant: "info",
            linkto: `/schedules/${this.schedule_id}`,
          },
          {
            icon: "fa-pencil",
            variant: "warning",
            linkto: `/schedules/${this.schedule_id}/sample-cohort`,
            title: "Manage Sample Cohort",
          },
        ],
        dropdown: {
          with_action: true,
          items: [
            {
              icon: "fa-cog",
              action_data: "clear",
              text: "Reset Schedule",
            },
            {
              icon: "fa-cog",
              action_data: "auto",
              text: "Run Auto-Schedule",
            },
          ],
        },
      };
    },
    current_instance() {
      return this.schedule_process.process_schedule_instrument_instances.find(
        (instance) =>
          instance.instrument_instance_id ===
            this.picked_instrument.instrument_instance_id &&
          instance.process_task_id === this.picked_task
      );
    },
    instrument_jig_update() {
      this.initialInstrumentJigPick();

      return this.picked_task + this.picked_instrument;
    },
    instrument_jig_options() {
      return this.instrument_jig_collection.models.length
        ? this.instrument_jig_collection.models
            .map(({ jig, instrument_id, id }) => {
              return {
                name: `${jig.name} x:${jig.x} y:${jig.y} z:${jig.z}`,
                data: {
                  instrument_id,
                  instrument_jig_id: id,
                  jig_id: jig.id,
                },
              };
            })
            .filter(
              (option) =>
                option.data.instrument_id ===
                this.picked_instrument.instrument_id
            )
        : [];
    },
    filret_client: {
      get() {
        return this.filter.client;
      },
      set(value) {
        this.filter.client = value;
      },
    },
    filret_rfid: {
      get() {
        return this.filter.rfid;
      },
      set(value) {
        this.filter.rfid = value;
      },
    },
    filret_turnaround: {
      get() {
        return this.filter.turnaround;
      },
      set(value) {
        this.filter.turnaround = value;
      },
    },
    filret_submission: {
      get() {
        return this.filter.submission;
      },
      set(value) {
        this.filter.submission = value;
      },
    },
    selected_sample_set: {
      get() {
        return this.filter.sample_set;
      },
      set(value) {
        this.filter.sample_set = value;
      },
    },
  },
  validations: {
    picked_instrument_jig: { required },
  },
  methods: {
    ...mapActions(["UPDATE_SCHEDULE_SAMPLE_LIST"]),
    async dropdownAction(action_type) {
      this.loading = true;

      try {
        await ScheduleRepository[`${action_type}Schedule`](this.schedule_id);

        await this.fetchSchedule();

        this.$bvToast.toast(
          `Schedule ${
            action_type === "clear" ? "Reset" : "Auto-Forming"
          } went successfully.`,
          {
            title: "Schedule Update successful.",
            variant: "success",
          }
        );
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Schedule Update Failed.",
          variant: "danger",
        });
      }

      this.loading = false;
    },
    initialInstrumentJigPick() {
      const check = this.instrument_jig_options.find(
        ({ data }) => data.jig_id === this.current_instance.jig_id
      );

      this.picked_instrument_jig = check ? check.data : null;
    },
    async updateInstrumentJig(value) {
      try {
        await ProcessScheduleRepository.updateJig(
          this.current_instance.id,
          value.instrument_jig_id
        );

        this.fetchSchedule();

        this.picked_instrument_jig = value;

        this.$bvToast.toast("Current Instrument jig was updated.", {
          title: "Update successful.",
          variant: "success",
        });
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Update Failed.",
          variant: "danger",
        });
      }
    },
    /**
     * Set the initial instrument selection.
     */
    initialInstrumentPick() {
      const { instrument_instance_id, process_task_id } =
        getInitialInstanceData(this.schedule_process) || {};

      // if instruments exist on the process: set an initial selection
      if (instrument_instance_id && this.schedule_process.instrument_instances) {
        const instrument_id = this.schedule_process.instrument_instances.find(
          (instance) => instance.id === instrument_instance_id
        ).instrument_id;
        this.picked_instrument = { instrument_instance_id, instrument_id };
      } else {
        this.picked_instrument = null;
      }

      this.picked_task = process_task_id;
      this.initialInstrumentJigPick();
    },
    changeTab() {
      const { instrument_instance_id, process_task_id } = getTabData(
        this.schedule_process,
        this.$children,
        5
      );
      const instrument_id = this.getInstrumentId(instrument_instance_id);

      this.picked_instrument = { instrument_instance_id, instrument_id };
      this.picked_task = process_task_id;
    },
    getInstrumentId(instance_id) {
      if (
        this.schedule_process.instrument_instances &&
        this.schedule_process.instrument_instances.length
      ) {
        return this.schedule_process.instrument_instances.find(
          (instance) => instance.id === instance_id
        ).instrument_id;
      } 
      return null;
    },
    getTaskInstances(task) {
      return getTaskInstances(this.schedule_process, task);
    },
    async fetchSchedule() {
      this.schedule = await getModel({
        model_type: "schedule",
        id: this.schedule_id,
        message: this.$bvToast,
      });
    },
    async init() {
      const connections = [
        "samples.submission.project.customer",
        "samples.submission.turnaround",
      ];

      await this.fetchSchedule();

      this.instrument_jig_collection = await getCollection({
        collection_type: "instrument_jig",
        filters: [
          {
            logic: "filterIn",
            field: "instrument_id",
            value: this.instrument_id_list,
          },
        ],
        message: this.$bvToast,
      });

      this.picked_process = this.schedule.process_schedules[0].id;

      this.initialInstrumentPick();

      this.sample_type = await getModel({
        model_type: "sample_type",
        id: this.schedule.sample_type_id,
        message: this.$bvToast,
      });

      const collection = await getCollection({
        collection_type: "schedule",
        filters: [
          {
            logic: "filterEquals",
            field: "id",
            value: this.schedule_id,
          },
        ],
        connections,
        message: this.$bvToast,
      });

      this.sample_collection = collection.models[0].getSamples();
      this.loading = false;
    },
    pickInstance(id) {
      const instrument_id = this.getInstrumentId(id);

      this.picked_instrument = { instrument_instance_id: id, instrument_id };
    },
    pickProcess(id) {
      this.picked_process = id;

      this.initialInstrumentPick();
    },
    clearFilters() {
      this.filter.client = null;
      this.filter.rfid = null;
      this.filter.turnaround = null;
      this.filter.submission = null;
    },
  },
  created() {
    this.loading = true;

    this.init();
  },
};
</script>

<style>
.picked {
  background-color: #20a8d8;
  color: white;
}
</style>
